import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Paintable, Size } from 'ff-canvas';
import { selectConfig3d } from '../_redux-variants';
import { Engine3d, loadEngine3d } from './helpers/loadEngine3d';
import { createEngine2d, Engine2d } from '@gift/common/helpers/createEngine2d';
import { WebsiteState } from '@gift/website-state/store';
import { TransformParameters } from '@gift/common/helpers/transformParameters';
import { useTranslation } from 'react-i18next';
import { Config3d } from '@gift/basic-scene-core/BasicVariantOptions';
import styles from '../BasicPreviewUi.module.scss';
import { PreviewUiElement } from '@gift/ui/components/preview-ui-element/PreviewUiElement';
import { Icon } from '@gift/ui/components/icons/Icon';
import { selectVariablePaintables } from '@gift/website-state/slices/variablesSlice';
import { AspectRatio } from '@gift/ui/components/aspect-ratio/AspectRatio';
import { Spinner } from '@gift/ui/components/loaders/spinner/Spinner';
import { useResize } from '@gift/ui/hooks/useResize';
import { selectTransformParametersForPreview } from '@gift/basic-scene-website/_redux-prefabs';

const _BasicPreview3d = ({
    variables, config3d,
    transformParameters
}: Props) => {

    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = useResize(containerRef);

    const canvas2dRef = useRef<HTMLCanvasElement>(null);
    const paintablesRef = useRef<Paintable[]>([]);
    const [engine2d, setEngine2d] = useState<Engine2d | null>(null);

    const canvas3dRef = useRef<HTMLCanvasElement>(null);
    const activeConfig3dRef = useRef<Config3d | null>(null);
    const [engine3d, setEngine3d] = useState<Engine3d | undefined>();

    const { t } = useTranslation();

    // 3D engine creation:
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (!engine3d || config3d !== activeConfig3dRef.current) {
                activeConfig3dRef.current = config3d;
                (async function () {
                    const newEngine3d = await loadEngine3d(canvas3dRef.current!, config3d!);
                    if (activeConfig3dRef.current === config3d) {
                        engine3d?.dispose();
                        setEngine3d(newEngine3d);
                        setEngine2d(null);
                    }
                }());
            }
        }
    }, [engine3d, config3d]);

    // 3D engine disposal on unmount component or when engine3d changed:
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (engine3d) {
                return () => {
                    engine3d?.dispose();
                };
            }
        }
    }, [engine3d]);

    // 2D backing engine creation:
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (containerSize) {
                if (engine3d && !engine2d?.is(containerSize, transformParameters)) {
                    setEngine2d(createEngine2d(
                        paintablesRef, canvas2dRef.current!, containerSize, transformParameters,
                        { onRender: engine3d.scene3d.changeTextureAsync }));
                }
            }
        }
    }, [engine3d, engine2d, containerSize, transformParameters]);

    // 3D engine resize:
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (containerSize && engine3d) {
                resize(canvas3dRef.current!, containerSize);
                engine3d.scene3d.updateDimensions(containerSize);
            }
        }
    }, [engine3d, containerSize]);

    // 2D backing engine paintables rerender:
    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {
            if (engine2d) {
                paintablesRef.current = variables;
                engine2d.requestRender();
            }
        }
    }, [engine2d, variables]);

    const canvasClassName = config3d !== activeConfig3dRef.current
        ? styles.hidden
        : styles.canvas;

    return (
        <>
            <div ref={containerRef} className={styles.container}>
                <AspectRatio width={transformParameters.sceneWidth} height={transformParameters.sceneHeight}>
                    {(!engine3d || config3d !== activeConfig3dRef.current) && (
                        <Spinner overlaySize='full' spinnerSize='medium' />
                    )}
                    <canvas ref={canvas3dRef} className={canvasClassName} />
                    <canvas ref={canvas2dRef} style={{ 'display': 'none' }} />
                </AspectRatio>
            </div>

            {engine3d && (
                <PreviewUiElement className={styles.tooltip}>
                    <Icon type='three-sixty' className={styles.icon} size='medium' />
                    <strong>{t('commons.rotate_image')}</strong>
                </PreviewUiElement>
            )}
        </>
    );
};

type Props = PropsWithChildren<{
    variables: Paintable[],
    config3d: Config3d,
    transformParameters: TransformParameters
}>;

const mapStateToProps = (state: WebsiteState) => ({
    variables: selectVariablePaintables(state, 'preview3d'),
    config3d: selectConfig3d(state)!,
    transformParameters: selectTransformParametersForPreview(state)
});

export const BasicPreview3d = connect(
    mapStateToProps
)(_BasicPreview3d);

const resize = (
    canvas3d: HTMLCanvasElement,
    containerSize: Size
) => {
    canvas3d.width = containerSize.width;
    canvas3d.height = containerSize.height;
    canvas3d.style.width = containerSize.width + 'px';
    canvas3d.style.height = containerSize.height + 'px';
};